<template>
  <transition name="upload-tray">
    <section
      v-if="batches.length"
      class="upload-tray"
      :class="{ 'upload-tray--minimized': isMinimized }"
      :style="trayOffset"
      role="region"
      aria-label="Document uploads">
      <v-card class="upload-tray__card" elevation="0">
        <!-- Header: always visible; the whole bar toggles the panel -->
        <div
          class="upload-tray__header"
          role="button"
          tabindex="0"
          :aria-expanded="String(!isMinimized)"
          @click="toggleMinimize"
          @keydown.enter.prevent="toggleMinimize"
          @keydown.space.prevent="toggleMinimize">
          <v-progress-circular
            v-if="hasActiveUploads"
            :value="overallPercent"
            :indeterminate="isIndeterminate"
            size="22"
            width="3"
            color="primary"
            class="mr-3 flex-shrink-0" />
          <v-icon v-else :color="summaryIcon.color" class="mr-3 flex-shrink-0">{{ summaryIcon.icon }}</v-icon>

          <div class="upload-tray__summary">
            <div class="upload-tray__title" aria-live="polite">{{ summaryTitle }}</div>
            <div v-if="summarySubtitle" class="upload-tray__subtitle">{{ summarySubtitle }}</div>
          </div>

          <v-btn
            icon
            small
            :aria-label="isMinimized ? 'Expand uploads' : 'Minimize uploads'"
            @click.stop="toggleMinimize">
            <v-icon small>{{ isMinimized ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
          <v-btn
            v-if="!hasActiveUploads"
            icon
            small
            aria-label="Close uploads"
            @click.stop="clearFinished">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-progress-linear
          v-if="hasActiveUploads && isMinimized"
          :value="overallPercent"
          :indeterminate="isIndeterminate"
          height="2"
          color="primary" />

        <!-- Body -->
        <v-expand-transition>
          <div v-show="!isMinimized">
            <v-divider />
            <div class="upload-tray__body">
              <div v-for="batch in batches" :key="batch.id" class="upload-batch">
                <div class="upload-batch__head">
                  <v-icon small :color="statusMeta(batch.status).color" class="mr-2">
                    {{ statusMeta(batch.status).icon }}
                  </v-icon>
                  <div class="upload-batch__label">
                    <span class="font-weight-medium">{{ batchTitle(batch) }}</span>
                    <span class="upload-batch__meta">{{ batchMeta(batch) }}</span>
                  </div>

                  <v-btn
                    v-if="batch.status === 'failed' || (batch.status === 'cancelled' && canRetry(batch))"
                    x-small
                    text
                    color="primary"
                    @click="retry(batch)">
                    <v-icon x-small left>mdi-refresh</v-icon>Retry
                  </v-btn>
                  <v-tooltip v-if="isCancellable(batch)" top>
                    <template #activator="{ on, attrs }">
                      <v-btn icon x-small v-bind="attrs" aria-label="Cancel upload" v-on="on" @click="cancel(batch)">
                        <v-icon small>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Cancel upload</span>
                  </v-tooltip>
                  <v-btn
                    v-else-if="!isActive(batch)"
                    icon
                    x-small
                    aria-label="Remove from list"
                    @click="remove(batch)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <div v-if="batch.error" class="upload-batch__error" role="alert">{{ batch.error }}</div>

                <ul class="upload-batch__files">
                  <li v-for="file in batch.files" :key="file.index" class="upload-file">
                    <v-icon x-small :color="fileStatusMeta(file.status).color" class="mr-2 flex-shrink-0">
                      {{ fileStatusMeta(file.status).icon }}
                    </v-icon>
                    <div class="upload-file__main">
                      <div class="upload-file__row">
                        <span class="upload-file__name" :title="file.name">{{ file.name }}</span>
                        <span class="upload-file__size">{{ fileStatusText(file) }}</span>
                      </div>
                      <v-progress-linear
                        v-if="file.status === 'uploading'"
                        :value="file.progress"
                        height="3"
                        rounded
                        color="primary"
                        class="mt-1"
                        :aria-label="`${file.name} upload progress`" />
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <template v-if="hasFinishedBatches">
              <v-divider />
              <div class="upload-tray__footer">
                <span class="upload-tray__hint">Uploads continue while you work.</span>
                <v-btn x-small text @click="clearFinished">Clear finished</v-btn>
              </div>
            </template>
          </div>
        </v-expand-transition>
      </v-card>
    </section>
  </transition>
</template>

<script>
  /**
   * Bytes to a readable size, 1024-based like the Add Document dialog's file list.
   * Not the shared formatFileSize in @/utils: it treats its smallest unit as MB.
   */
  function formatFileSize(bytes, decimals = 1) {
    if (!bytes || bytes < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${i === 0 ? value : value.toFixed(decimals)} ${units[i]}`;
  }

  const BATCH_STATUS = {
    queued: { icon: 'mdi-clock-outline', color: 'grey', label: 'Waiting' },
    checking: { icon: 'mdi-file-search-outline', color: 'primary', label: 'Checking names' },
    uploading: { icon: 'mdi-cloud-upload-outline', color: 'primary', label: 'Uploading' },
    saving: { icon: 'mdi-content-save-outline', color: 'primary', label: 'Saving' },
    completed: { icon: 'mdi-check-circle', color: 'success', label: 'Uploaded' },
    failed: { icon: 'mdi-alert-circle', color: 'error', label: 'Failed' },
    cancelled: { icon: 'mdi-cancel', color: 'grey', label: 'Cancelled' },
  };

  const FILE_STATUS = {
    queued: { icon: 'mdi-file-outline', color: 'grey' },
    uploading: { icon: 'mdi-file-upload-outline', color: 'primary' },
    done: { icon: 'mdi-check', color: 'success' },
    skipped: { icon: 'mdi-debug-step-over', color: 'grey' },
    failed: { icon: 'mdi-alert-circle-outline', color: 'error' },
    cancelled: { icon: 'mdi-cancel', color: 'grey' },
  };

  /** DownloadManager.vue is docked in the same corner; its widths are fixed there. */
  const DOWNLOAD_MANAGER_WIDTH = { open: 400, minimized: 280 };
  const CORNER_GAP = 20;

  /**
   * Docked, minimizable panel for background document uploads (DOCUMENT_UPLOADS store module).
   * Mounted once in App.vue so uploads stay visible across routes.
   */
  export default {
    name: 'DocumentUploadTray',
    computed: {
      batches() {
        return this.$store.getters['DOCUMENT_UPLOADS/GET_BATCHES'];
      },
      isMinimized() {
        return this.$store.getters['DOCUMENT_UPLOADS/GET_IS_MINIMIZED'];
      },
      activeBatches() {
        return this.$store.getters['DOCUMENT_UPLOADS/GET_ACTIVE_BATCHES'];
      },
      hasActiveUploads() {
        return this.$store.getters['DOCUMENT_UPLOADS/HAS_ACTIVE_UPLOADS'];
      },
      hasFinishedBatches() {
        return this.$store.getters['DOCUMENT_UPLOADS/HAS_FINISHED_BATCHES'];
      },
      isDownloadManagerVisible() {
        return this.$store.getters['DOWNLOAD_MANAGER/GET_IS_VISIBLE'];
      },
      isDownloadManagerMinimized() {
        return this.$store.getters['DOWNLOAD_MANAGER/GET_IS_MINIMIZED'];
      },
      trayOffset() {
        if (!this.isDownloadManagerVisible) return null;
        const width = this.isDownloadManagerMinimized ? DOWNLOAD_MANAGER_WIDTH.minimized : DOWNLOAD_MANAGER_WIDTH.open;
        return { right: `${width + CORNER_GAP * 2}px` };
      },
      activeTotals() {
        return this.activeBatches.reduce(
          (sum, b) => ({ loaded: sum.loaded + b.loadedBytes, total: sum.total + b.totalBytes, speed: sum.speed + b.bytesPerSecond }),
          { loaded: 0, total: 0, speed: 0 },
        );
      },
      overallPercent() {
        const { loaded, total } = this.activeTotals;
        return total ? Math.round((loaded / total) * 100) : 0;
      },
      /** Waiting on a name check or the server, not on the transfer. */
      isIndeterminate() {
        return this.activeBatches.length > 0 && this.activeBatches.every((b) => b.status !== 'uploading');
      },
      activeFileCount() {
        return this.activeBatches.reduce((n, b) => n + b.files.filter((f) => f.status !== 'skipped').length, 0);
      },
      summaryTitle() {
        if (this.hasActiveUploads) {
          const files = this.activeFileCount;
          const label = files === 1 ? 'document' : 'documents';
          if (this.activeBatches.some((b) => b.status === 'checking')) return `Preparing ${files} ${label}…`;
          if (this.activeBatches.every((b) => b.status === 'saving' || b.status === 'queued')) return `Saving ${files} ${label}…`;
          return `Uploading ${files} ${label} · ${this.overallPercent}%`;
        }
        const failed = this.batches.filter((b) => b.status === 'failed').length;
        if (failed) return failed === 1 ? '1 upload failed' : `${failed} uploads failed`;
        const done = this.batches
          .filter((b) => b.status === 'completed')
          .reduce((n, b) => n + b.files.filter((f) => f.status === 'done').length, 0);
        if (done) return done === 1 ? '1 file uploaded' : `${done} files uploaded`;
        return 'Uploads cancelled';
      },
      summarySubtitle() {
        if (!this.hasActiveUploads) return '';
        const { loaded, total, speed } = this.activeTotals;
        if (!total || !speed || this.isIndeterminate) return `${formatFileSize(total, 1)} total`;
        const seconds = (total - loaded) / speed;
        return `${formatFileSize(loaded, 1)} of ${formatFileSize(total, 1)} · ${this.formatEta(seconds)} left`;
      },
      summaryIcon() {
        if (this.batches.some((b) => b.status === 'failed')) return { icon: 'mdi-alert-circle', color: 'error' };
        if (this.batches.some((b) => b.status === 'completed')) return { icon: 'mdi-check-circle', color: 'success' };
        return { icon: 'mdi-cancel', color: 'grey' };
      },
    },
    watch: {
      hasActiveUploads: {
        immediate: true,
        handler(active) {
          if (active) window.addEventListener('beforeunload', this.onBeforeUnload);
          else window.removeEventListener('beforeunload', this.onBeforeUnload);
        },
      },
    },
    beforeDestroy() {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
    },
    methods: {
      /** Leaving the page would abort the transfer and lose the files. */
      onBeforeUnload(event) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      },
      toggleMinimize() {
        this.$store.dispatch('DOCUMENT_UPLOADS/TOGGLE_MINIMIZE');
      },
      cancel(batch) {
        this.$store.dispatch('DOCUMENT_UPLOADS/CANCEL', batch.id);
      },
      retry(batch) {
        this.$store.dispatch('DOCUMENT_UPLOADS/RETRY', batch.id);
      },
      remove(batch) {
        this.$store.dispatch('DOCUMENT_UPLOADS/REMOVE', batch.id);
      },
      clearFinished() {
        this.$store.dispatch('DOCUMENT_UPLOADS/CLEAR_FINISHED');
      },
      isCancellable(batch) {
        return this.$store.getters['DOCUMENT_UPLOADS/IS_CANCELLABLE'](batch);
      },
      isActive(batch) {
        return ['queued', 'checking', 'uploading', 'saving'].includes(batch.status);
      },
      /** A batch cancelled from the name-conflict prompt was the user's choice; one cancelled mid-transfer can be resumed. */
      canRetry(batch) {
        return batch.files.some((f) => f.status === 'cancelled');
      },
      statusMeta(status) {
        return BATCH_STATUS[status] || BATCH_STATUS.queued;
      },
      fileStatusMeta(status) {
        return FILE_STATUS[status] || FILE_STATUS.queued;
      },
      batchTitle(batch) {
        const status = this.statusMeta(batch.status).label;
        if (batch.kind === 'version') return `${status} · new version`;
        const count = batch.files.length;
        return `${status} · ${count} ${count === 1 ? 'file' : 'files'}`;
      },
      batchMeta(batch) {
        const parts = [];
        if (batch.kind === 'version') {
          if (batch.documentLabel) parts.push(batch.documentLabel);
          if (batch.revisionNumber) parts.push(`Rev ${batch.revisionNumber}`);
        }
        if (batch.scopeLabel) parts.push(batch.scopeLabel);
        if (batch.status === 'completed') {
          const skipped = batch.files.filter((f) => f.status === 'skipped').length;
          if (skipped) parts.push(`${skipped} skipped`);
        }
        return parts.join(' · ');
      },
      fileStatusText(file) {
        if (file.status === 'uploading') return `${file.progress}%`;
        if (file.status === 'skipped') return 'Skipped';
        if (file.status === 'failed') return 'Failed';
        if (file.status === 'cancelled') return 'Cancelled';
        return formatFileSize(file.size, 1);
      },
      formatEta(seconds) {
        if (!isFinite(seconds) || seconds <= 0) return 'a moment';
        if (seconds < 60) return `${Math.ceil(seconds)}s`;
        if (seconds < 3600) return `${Math.ceil(seconds / 60)} min`;
        return `${Math.floor(seconds / 3600)}h ${Math.ceil((seconds % 3600) / 60)}m`;
      },
    },
  };
</script>

<style scoped lang="scss">
  .upload-tray {
    position: fixed;
    right: 20px;
    bottom: 20px;
    width: 380px;
    max-width: calc(100vw - 32px);
    // Above the viewer and side panels, below Vuetify dialogs (z-index 202+): the name-conflict
    // prompt an upload opens must cover the tray, not the other way round.
    z-index: 200;
    transition: right var(--ds-transition-panel, 200ms ease), width var(--ds-transition-panel, 200ms ease);
    font-family: var(--ds-font-family-body);
  }

  .upload-tray--minimized {
    width: 320px;
  }

  .upload-tray__card {
    border-radius: var(--ds-radius-md, 8px) !important;
    box-shadow: var(--ds-shadow-4) !important;
    border: 1px solid var(--ds-border-default);
    overflow: hidden;
  }

  .upload-tray__header {
    display: flex;
    align-items: center;
    min-height: 52px;
    padding: var(--ds-space-sm, 8px) var(--ds-space-sm, 8px) var(--ds-space-sm, 8px) var(--ds-space-md, 16px);
    cursor: pointer;
    user-select: none;

    &:hover {
      background: var(--ds-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--v-primary-base);
      outline-offset: -2px;
    }
  }

  .upload-tray__summary {
    flex: 1;
    min-width: 0;
  }

  .upload-tray__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .upload-tray__subtitle {
    font-size: 12px;
    opacity: 0.7;
    font-variant-numeric: tabular-nums;
  }

  .upload-tray__body {
    max-height: min(360px, 50vh);
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .upload-batch {
    padding: var(--ds-space-sm, 8px) var(--ds-space-md, 16px);

    & + & {
      border-top: 1px solid var(--ds-border-subtle);
    }
  }

  .upload-batch__head {
    display: flex;
    align-items: center;
    gap: 2px;
    min-height: 28px;
  }

  .upload-batch__label {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    font-size: 13px;
    line-height: 1.3;
  }

  .upload-batch__meta {
    font-size: 11px;
    opacity: 0.65;
  }

  .upload-batch__error {
    margin: 4px 0 0 24px;
    font-size: 12px;
    color: var(--v-error-base);
    word-break: break-word;
  }

  .upload-batch__files {
    list-style: none;
    margin: 4px 0 0;
    padding: 0 0 0 24px;
  }

  .upload-file {
    display: flex;
    align-items: flex-start;
    padding: 3px 0;
  }

  .upload-file__main {
    flex: 1;
    min-width: 0;
  }

  .upload-file__row {
    display: flex;
    align-items: baseline;
    gap: var(--ds-space-sm, 8px);
    font-size: 12px;
  }

  .upload-file__name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .upload-file__size {
    flex-shrink: 0;
    opacity: 0.65;
    font-variant-numeric: tabular-nums;
  }

  .upload-tray__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px var(--ds-space-sm, 8px) 4px var(--ds-space-md, 16px);
  }

  .upload-tray__hint {
    font-size: 11px;
    opacity: 0.6;
  }

  .upload-tray-enter-active,
  .upload-tray-leave-active {
    transition: opacity 200ms ease, transform 200ms ease;
  }

  .upload-tray-enter,
  .upload-tray-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }

  @media (prefers-reduced-motion: reduce) {
    .upload-tray,
    .upload-tray-enter-active,
    .upload-tray-leave-active {
      transition: none;
    }
  }
</style>
